// src/modules/app-views/customers/pages/Init/components/CustomersFilterPopover/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { useListContext } from 'components/core/ListProvider';
import { useCallback } from 'react';
import dayjs from 'dayjs';
import translateX from 'utils/translateX';
import { getDateFormatByLocale } from 'utils/helpers';

import { Button, DatePicker, Select } from 'antd';

import './CustomersFilterPopover.less';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const ENTITY_TYPE_OPTIONS = [
  { value: 'person', label: 'individual_person' },
  { value: 'company', label: 'legal_person' },
];

// Brazilian states (UF) — value matches the stored address.region abbreviation
const BRAZIL_STATES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

// *******************************************************************************
// COMPONENT
// *******************************************************************************
const CustomersFilterPopover = ({ onRequestClose, onOpenDrawer }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { setFilter, setDrawerFilter, clearFilters, query } = useListContext() || {};
  const format = getDateFormatByLocale();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. DERIVED VALUES — read directly from context (no local state)
  // ─────────────────────────────────────────────────────────────────────────────
  // Base filter carries both types (['person','company']); a single value means
  // the quick filter is active.
  const entityTypeValue = (() => {
    const v = query?.filter?.entityType;
    return Array.isArray(v) && v.length === 1 ? v[0] : undefined;
  })();

  const stateValue = (() => {
    const v = query?.filter?.['address.region'];
    return Array.isArray(v) && v.length === 1 ? v[0] : undefined;
  })();

  const dateRangeValue = (() => {
    const range = query?.drawer?.dateRange;
    if (range?.length === 2 && range[0] && range[1]) return [dayjs(range[0]), dayjs(range[1])];
    return null;
  })();

  // Date presets for RangePicker (identical to invoices popover calendar)
  const datePresets = [
    { label: translateX('today'), value: [dayjs(), dayjs()] },
    {
      label: translateX('this_week'),
      value: [dayjs().startOf('week'), dayjs().endOf('week')],
    },
    {
      label: translateX('this_month'),
      value: [dayjs().startOf('month'), dayjs().endOf('month')],
    },
    {
      label: translateX('last_month'),
      value: [
        dayjs().subtract(1, 'month').startOf('month'),
        dayjs().subtract(1, 'month').endOf('month'),
      ],
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS — apply immediately on change (no deferred apply)
  // ─────────────────────────────────────────────────────────────────────────────
  const handleEntityTypeChange = useCallback(
    value => {
      setFilter?.('entityType', value != null ? [value] : undefined);
    },
    [setFilter]
  );

  const handleStateChange = useCallback(
    value => {
      setFilter?.('address.region', value != null ? [value] : undefined);
    },
    [setFilter]
  );

  const handleDateRangeChange = useCallback(
    dates => {
      if (dates?.[0] && dates?.[1]) {
        setDrawerFilter?.({
          dateRange: [dates[0].startOf('day').toISOString(), dates[1].endOf('day').toISOString()],
        });
      } else {
        setDrawerFilter?.({ dateRange: undefined });
      }
    },
    [setDrawerFilter]
  );

  const handleClear = useCallback(() => {
    clearFilters?.();
    onRequestClose?.();
  }, [clearFilters, onRequestClose]);

  const handleAdvancedFilters = useCallback(() => {
    onRequestClose?.();
    onOpenDrawer?.();
  }, [onRequestClose, onOpenDrawer]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="customers-filter-popover">
      <div className="customers-filter-popover__title">{translateX('quick_filters')}</div>

      <div className="customers-filter-popover__row">
        <div className="customers-filter-popover__field">
          <div className="customers-filter-popover__label">{translateX('type')}</div>
          <Select
            allowClear
            size="middle"
            value={entityTypeValue}
            onChange={handleEntityTypeChange}
            options={ENTITY_TYPE_OPTIONS.map(o => ({
              value: o.value,
              label: translateX(o.label),
            }))}
            placeholder={translateX('type')}
          />
        </div>

        <div className="customers-filter-popover__field">
          <div className="customers-filter-popover__label">{translateX('state')}</div>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            size="middle"
            value={stateValue}
            onChange={handleStateChange}
            options={BRAZIL_STATES.map(uf => ({ value: uf, label: uf }))}
            placeholder={translateX('state')}
          />
        </div>
      </div>

      <div className="customers-filter-popover__row">
        <div className="customers-filter-popover__field">
          <div className="customers-filter-popover__label">{translateX('registration_date')}</div>
          <DatePicker.RangePicker
            size="middle"
            format={format}
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            presets={datePresets}
          />
        </div>
      </div>

      <div className="customers-filter-popover__divider">
        <Button
          type="link"
          size="small"
          className="customers-filter-popover__advanced-btn"
          onClick={handleAdvancedFilters}
        >
          {`${translateX('advanced_filters')} →`}
        </Button>
        <div className="customers-filter-popover__actions">
          <Button size="small" onClick={handleClear}>
            {translateX('clear')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CustomersFilterPopover;
